export interface EloChartPoint {
  /** ELO rating at this point in the series. */
  elo: number;
  /** X-axis label for this point. */
  label: string;
}

export interface EloChartProps {
  /** Ordered ELO series; the chart needs at least two points to render. */
  data: EloChartPoint[];
  /** SVG viewBox height in px. */
  height?: number;
  /** Accessible label for the chart (kept for parity with the original artifact). */
  label?: string;
}

/**
 * Reusable SVG line chart for ELO development.
 * Extracted verbatim from the original artifact (legacy/App.jsx); markup unchanged so
 * styling is identical.
 */
export function EloChart({
  data,
  height = 100,
  // `label` is accepted for parity with the original artifact but unused in markup.
}: EloChartProps) {
  if (!Array.isArray(data) || data.length < 2) {
    return (
      <div className="text-xs text-ink-3 italic text-center py-4">
        Ikke nok data til å vise grafen ennå
      </div>
    );
  }

  const padding = { top: 12, right: 12, bottom: 22, left: 38 };
  const width = 600; // SVG viewBox width, scaled responsively
  const chartW = width - padding.left - padding.right;
  const chartH = height - padding.top - padding.bottom;

  const values = data.map((d) => d.elo);
  const minVal = Math.min(...values);
  const maxVal = Math.max(...values);
  const range = Math.max(maxVal - minVal, 1);
  // Pad a little to avoid lines sitting right on the edges
  const yMin = minVal - range * 0.1;
  const yMax = maxVal + range * 0.1;
  const yRange = yMax - yMin;

  const points = data.map((d, i) => ({
    x: padding.left + (i / (data.length - 1)) * chartW,
    y: padding.top + chartH - ((d.elo - yMin) / yRange) * chartH,
    elo: d.elo,
    label: d.label,
  }));

  const linePath = points
    .map((p, i) => `${i === 0 ? 'M' : 'L'}${p.x.toFixed(1)} ${p.y.toFixed(1)}`)
    .join(' ');
  const areaPath = `${linePath} L${points[points.length - 1].x.toFixed(1)} ${padding.top + chartH} L${points[0].x.toFixed(1)} ${padding.top + chartH} Z`;

  const startElo = data[0].elo;
  const endElo = data[data.length - 1].elo;
  const trend = endElo - startElo;
  const lineColor = trend > 0 ? '#84cc16' : trend < 0 ? '#f43f5e' : '#8a948c';
  const fillColor =
    trend > 0
      ? 'rgba(132, 204, 22, 0.18)'
      : trend < 0
        ? 'rgba(244, 63, 94, 0.18)'
        : 'rgba(138, 148, 140, 0.15)';

  // Reference line for the starting ELO
  const startY = padding.top + chartH - ((startElo - yMin) / yRange) * chartH;

  return (
    <div className="w-full">
      <svg
        viewBox={`0 0 ${width} ${height}`}
        className="w-full h-auto"
        preserveAspectRatio="none"
        style={{ maxHeight: `${height}px` }}
      >
        {/* Y-axis labels */}
        <text
          x={padding.left - 6}
          y={padding.top + 4}
          textAnchor="end"
          fontSize="10"
          fontFamily="JetBrains Mono, monospace"
          fill="#8a948c"
        >
          {Math.round(yMax)}
        </text>
        <text
          x={padding.left - 6}
          y={padding.top + chartH + 4}
          textAnchor="end"
          fontSize="10"
          fontFamily="JetBrains Mono, monospace"
          fill="#8a948c"
        >
          {Math.round(yMin)}
        </text>

        {/* Dashed reference line for the starting ELO */}
        <line
          x1={padding.left}
          y1={startY}
          x2={padding.left + chartW}
          y2={startY}
          stroke="#cbd5d1"
          strokeDasharray="3 3"
          strokeWidth="1"
        />
        <text
          x={padding.left + chartW + 4}
          y={startY + 3}
          fontSize="9"
          fontFamily="JetBrains Mono, monospace"
          fill="#8a948c"
        >
          start
        </text>

        {/* Fill under the line */}
        <path d={areaPath} fill={fillColor} />

        {/* The line itself */}
        <path
          d={linePath}
          stroke={lineColor}
          strokeWidth="2.2"
          fill="none"
          strokeLinecap="round"
          strokeLinejoin="round"
          vectorEffect="non-scaling-stroke"
        />

        {/* Points */}
        {points.map((p, i) => (
          <circle
            key={i}
            cx={p.x}
            cy={p.y}
            r="3.5"
            fill="white"
            stroke={lineColor}
            strokeWidth="2"
            vectorEffect="non-scaling-stroke"
          >
            <title>{`${p.label}: ${p.elo}`}</title>
          </circle>
        ))}

        {/* First and last X-axis labels */}
        <text
          x={points[0].x}
          y={padding.top + chartH + 16}
          textAnchor="middle"
          fontSize="9"
          fontFamily="JetBrains Mono, monospace"
          fill="#8a948c"
        >
          {points[0].label}
        </text>
        <text
          x={points[points.length - 1].x}
          y={padding.top + chartH + 16}
          textAnchor="middle"
          fontSize="9"
          fontFamily="JetBrains Mono, monospace"
          fill="#8a948c"
        >
          {points[points.length - 1].label}
        </text>
      </svg>
    </div>
  );
}
